import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import clsx from "clsx";
import { ENHANCEMENT_OPTIONS } from "@components/shared/form/ai/enhancementPrompts";
import SolaIconBadge from "@components/shared/SolaIconBadge";
import styles from "./RewriteWithAIPanel.module.css";

// Matches .panel's `width` in RewriteWithAIPanel.module.css — used to
// right-align the portaled panel against its anchor without needing to
// measure the (not-yet-rendered) panel itself.
const PANEL_WIDTH = 250;

// The floating "Rewrite with AI" panel shared by every editable AI field
// (RichAIField, RichTextAIField, and AITextField's compact mode) — a small
// header, a grid of preset prompts, and a custom-prompt fallback. Without
// `anchorRef`, expects to be rendered inside a `position: relative`
// container so it anchors directly under whatever triggered it (the
// original behaviour, kept for callers that don't need to escape a
// scrolling/clipping ancestor). With `anchorRef`, it instead portals to
// document.body and positions itself from the anchor's own bounding box —
// used by fields that can sit inside an overflow-clipped container (e.g.
// the email preview's rounded browser frame), so the panel always renders
// fully visible on top of everything else instead of getting cut off.
export default function RewriteWithAIPanel({
  onClose,
  onOptionSelect,
  onCustomSubmit,
  isEnhancing = false,
  excludePrompts = [],
  anchorRef,
  panelRef,
}) {
  const [customPrompt, setCustomPrompt] = useState("");
  const [anchoredStyle, setAnchoredStyle] = useState(null);
  // Caller-supplied when it needs to recognize clicks inside the (possibly
  // portaled, so no longer a DOM descendant of the caller's own container)
  // panel as "not an outside click" — falls back to a local ref otherwise.
  const internalPanelRef = useRef(null);
  const resolvedPanelRef = panelRef || internalPanelRef;

  useLayoutEffect(() => {
    if (!anchorRef?.current) return undefined;

    const updatePosition = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const left = Math.max(
        8,
        Math.min(rect.right + window.scrollX - PANEL_WIDTH, window.innerWidth + window.scrollX - PANEL_WIDTH - 8)
      );
      // Always anchors below the trigger — if there isn't enough room
      // before the bottom of the viewport, the panel scrolls internally
      // (`Math.max(0, ...)`, not a larger floor: the space actually
      // available is the hard ceiling, since the point is to never exceed
      // it) rather than running off-screen or flipping direction.
      //
      // The page's own fixed bottom action bar (e.g. "Send to contacts")
      // sits on top of part of that space without being reflected in
      // window.innerHeight — reserve room for a bar that size, plus the
      // 24px of breathing room the last item still needs below it, or the
      // panel's own maxHeight lets its lower rows render underneath the
      // bar with no way to scroll down far enough to reach them.
      const BOTTOM_BAR_ALLOWANCE = 68;
      const BOTTOM_MARGIN = 24;
      setAnchoredStyle({
        position: "absolute",
        left,
        top: rect.bottom + window.scrollY + 8,
        maxHeight: Math.max(0, window.innerHeight - rect.bottom - BOTTOM_BAR_ALLOWANCE - BOTTOM_MARGIN),
        overflowY: "auto",
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef]);

  const options = ENHANCEMENT_OPTIONS.filter(
    (option) => option.id !== "custom" && !excludePrompts.includes(option.id)
  );

  const handleCustomSubmit = () => {
    if (isEnhancing || !customPrompt.trim()) return;
    onCustomSubmit(customPrompt);
    setCustomPrompt("");
  };

  // `!!anchorRef` (not a function call) avoids the "no passing refs into
  // functions during render" lint rule that a `Boolean(anchorRef)` call, or
  // referencing `anchorRef` directly inside clsx(...)'s arguments, trips.
  const isAnchored = !!anchorRef;

  const panel = (
    <div
      ref={resolvedPanelRef}
      className={clsx(styles.panel, isAnchored && styles.panelPortaled)}
      style={isAnchored ? anchoredStyle || { visibility: "hidden" } : undefined}
    >
      <div className={styles.panelHeader}>
        <SolaIconBadge size={16} variant="background" />
        <span className={styles.panelTitle}>Rewrite with AI</span>
        <button
          type="button"
          className={styles.panelClose}
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      <div className={styles.list}>
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              className={styles.promptButton}
              disabled={isEnhancing}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onOptionSelect(option.id)}
            >
              <Icon size={14} />
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
      <div className={styles.customRow}>
        <input
          className={styles.customInput}
          value={customPrompt}
          onChange={(event) => setCustomPrompt(event.target.value)}
          placeholder="Or describe a change..."
          disabled={isEnhancing}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing) {
              event.preventDefault();
              handleCustomSubmit();
            }
          }}
        />
        <button
          type="button"
          className={styles.goButton}
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleCustomSubmit}
          disabled={!customPrompt.trim() || isEnhancing}
          title="Rewrite with this prompt"
        >
          <SolaIconBadge size={20} />
        </button>
      </div>
    </div>
  );

  return isAnchored ? createPortal(panel, document.body) : panel;
}
